import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button, HelperText, Icon, Spinner, TextInput, Tooltip, Typography } from 'gu-ui-kit'
import { EMAIL_REQUEST_CONFIRMATION } from 'modules/Auth/types'
import PropTypes from 'prop-types'

const TIMEOUT = 60 // sec

const Email = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    email = '',
    loaded,
    loading = false,
    is_email_confirmed = null,
  } = useSelector((state) => state['user/profile'])

  const [sendAgain, setSendAgain] = useState(true)
  const [sec, setSec] = useState(TIMEOUT)
  const timeOutRef = useRef(null)
  const countDownRef = useRef(null)

  const startCountdown = useCallback(() => {
    countDownRef.current = setTimeout(() => {
      setSec((prevState) => prevState - 1)
      startCountdown()
    }, 1000)
  }, [countDownRef.current, sendAgain])

  const startTimeout = useCallback(() => {
    clearTimeout(timeOutRef.current)
    setSendAgain(false)
    startCountdown()
    timeOutRef.current = setTimeout(() => {
      clearTimeout(countDownRef.current)
      setSendAgain(true)
      setSec(TIMEOUT)
    }, TIMEOUT * 1000)
  }, [timeOutRef.current, sendAgain, countDownRef.current])

  const handleClick = useCallback(() => {
    dispatch({ type: EMAIL_REQUEST_CONFIRMATION })
    setSendAgain(false)
    startTimeout()
  }, [])

  useEffect(
    () => () => {
      clearTimeout(timeOutRef.current)
      clearTimeout(countDownRef.current)
    },
    [],
  )

  return (
    <>
      <TextInput
        autocomplete="off"
        fullWidth
        label={t('user::settings.profile.email.label')}
        type="text"
        value={email}
        readOnly
        size={'l'}
        InputProps={{
          startAdornment:
            loading && !email.length ? (
              <Spinner
                width={16}
                color={'Label-GU-Primary'}
              />
            ) : null,
          endAdornment: loading ? null : (
            <Tooltip
              title={t(
                `user::settings.profile.email.status.${
                  is_email_confirmed ? 'approved' : 'disapproved'
                }`,
              )}
              placement={'bottom'}
            >
              <Icon
                name={`status ${is_email_confirmed ? 'done' : 'warning2'}`}
                size={'m'}
                color={is_email_confirmed ? 'Accent-Confirm-Primary' : 'Accent-Error-Primary'}
              />
            </Tooltip>
          ),
        }}
      />
      {loaded && !is_email_confirmed && (
        <HelperText simpleHelperStyle>
          {sendAgain ? (
            <Button
              size={'s'}
              variant={'ghost'}
              link
              onClick={handleClick}
            >
              {t('user::settings.profile.email.helperText')}
            </Button>
          ) : (
            <Typography
              variant={'Body-10'}
              color={'Label-Fifth'}
            >
              {t('user::settings.profile.email.timeout', {
                sec: `00:${sec < 10 ? '0' + sec : sec}`,
              })}
            </Typography>
          )}
        </HelperText>
      )}
    </>
  )
}

Email.propTypes = {
  onSuccess: PropTypes.func,
  handleSubmit: PropTypes.func,
  autofill: PropTypes.func,
  submitting: PropTypes.bool,
  form: PropTypes.string,
}

export default Email
